import { useEffect, useState } from 'react'

import { ApiError } from '../../lib/api'
import { biz, type Company } from '../../lib/business'
import { useToast } from '../../app/Toasts'
import { useAuth } from '../../app/AuthContext'
import { Field } from './ContactsPage'

export default function SettingsPage() {
  const { push } = useToast()
  const { can } = useAuth()
  const [c, setC] = useState<Company | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => { biz.company().then(setC).catch(() => {}) }, [])

  if (!c) return <div className="skeleton" style={{ height: 260 }} />

  const set = (patch: Partial<Company>) => setC({ ...c, ...patch })

  const save = async () => {
    setBusy(true)
    try {
      setC(await biz.saveCompany(c))
      push('success', 'Company details saved')
    } catch (e) {
      push('error', 'Could not save', e instanceof ApiError ? e.message : undefined)
    } finally { setBusy(false) }
  }

  const readOnly = !can('org:manage')

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 860 }}>
      {!c.gst_configured && (
        <div className="card" style={{ borderLeft: '3px solid var(--warn)' }}>
          <div className="card-body">
            <div style={{ fontWeight: 600, marginBottom: 2 }}>GSTIN required</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Your GSTIN carries your state code, and that decides whether a sale
              is taxed as CGST + SGST or IGST. Until it is set, raising quotations
              and invoices is blocked rather than risk issuing them with the wrong
              tax heads.
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <span className="card-title">Company</span>
          <div className="grow" />
          {c.gst_configured && (
            <span className="pill pill-ok">
              GST {c.state_code} · {c.state_name}
            </span>
          )}
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Trading name" required>
              <input className="input" value={c.name} disabled={readOnly}
                onChange={(e) => set({ name: e.target.value })} />
            </Field>
            <Field label="GSTIN" required hint="15 characters; checksum verified on save">
              <input className="input input-mono" maxLength={15} disabled={readOnly}
                value={c.gstin ?? ''}
                onChange={(e) => set({ gstin: e.target.value.toUpperCase() })} />
            </Field>
            <Field label="Legal name" hint="As printed on your invoices">
              <input className="input" value={c.legal_name ?? ''} disabled={readOnly}
                onChange={(e) => set({ legal_name: e.target.value })} />
            </Field>
            <Field label="PAN">
              <input className="input input-mono" value={c.pan ?? ''} disabled={readOnly}
                onChange={(e) => set({ pan: e.target.value.toUpperCase() })} />
            </Field>
            <Field label="Address">
              <textarea className="textarea" rows={2} value={c.address ?? ''} disabled={readOnly}
                onChange={(e) => set({ address: e.target.value })} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <Field label="City">
                <input className="input" value={c.city ?? ''} disabled={readOnly}
                  onChange={(e) => set({ city: e.target.value })} />
              </Field>
              <Field label="State">
                <input className="input" value={c.state ?? ''} disabled={readOnly}
                  onChange={(e) => set({ state: e.target.value })} />
              </Field>
              <Field label="PIN">
                <input className="input" value={c.pincode ?? ''} disabled={readOnly}
                  onChange={(e) => set({ pincode: e.target.value })} />
              </Field>
            </div>
            <Field label="Email">
              <input className="input" value={c.email ?? ''} disabled={readOnly}
                onChange={(e) => set({ email: e.target.value })} />
            </Field>
            <Field label="Phone">
              <input className="input" value={c.phone ?? ''} disabled={readOnly}
                onChange={(e) => set({ phone: e.target.value })} />
            </Field>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head"><span className="card-title">Bank details</span></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Bank name">
              <input className="input" value={c.bank_name ?? ''} disabled={readOnly}
                onChange={(e) => set({ bank_name: e.target.value })} />
            </Field>
            <Field label="Account number">
              <input className="input input-mono" value={c.bank_account ?? ''} disabled={readOnly}
                onChange={(e) => set({ bank_account: e.target.value })} />
            </Field>
            <Field label="IFSC">
              <input className="input input-mono" value={c.bank_ifsc ?? ''} disabled={readOnly}
                onChange={(e) => set({ bank_ifsc: e.target.value.toUpperCase() })} />
            </Field>
            <Field label="UPI ID">
              <input className="input input-mono" value={c.upi_id ?? ''} disabled={readOnly}
                onChange={(e) => set({ upi_id: e.target.value })} />
            </Field>
          </div>
          <div style={{ marginTop: 12 }}>
            <Field label="Default invoice terms">
              <textarea className="textarea" rows={2} value={c.invoice_terms ?? ''}
                disabled={readOnly}
                onChange={(e) => set({ invoice_terms: e.target.value })} />
            </Field>
          </div>
          {!readOnly && (
            <div className="row" style={{ marginTop: 14 }}>
              <button className="btn btn-primary" onClick={save} disabled={busy}>
                {busy && <span className="spinner" />}Save
              </button>
            </div>
          )}
          {readOnly && (
            <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
              Only the workspace owner can change these.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
